// Static milestone content for the Journey page
// Replaces Gemini AI calls with pre-written, comprehensive content
//
// REFERENCES & SOURCES:
// All milestone information is based on peer-reviewed medical guidelines.
// Each milestone includes source and learnMoreUrl for direct reference.
//
// Primary Sources Used:
// - American College of Obstetricians and Gynecologists (ACOG) - www.acog.org
// - March of Dimes - www.marchofdimes.org
// - American Pregnancy Association - americanpregnancy.org
// - Centers for Disease Control and Prevention (CDC) - www.cdc.gov
//
// Medical Guidelines Referenced:
// - ACOG Practice Bulletin: Antepartum Fetal Surveillance
// - ACOG FAQ: Ultrasound Exams
// - ACOG FAQ: Prenatal Genetic Screening Tests
// - CDC Guidelines: Group B Strep and Tdap Vaccination
// - March of Dimes: Prenatal Care and Tests
//
// Disclaimer: This content is for informational purposes only and does not
// constitute medical advice. Always consult your healthcare provider.

export interface MilestoneData {
    week: number;
    title: string;
    emoji: string;
    description: string;
    partnerTip: string;
    pregnantTip: string;
    logistics: string;
    whatToExpect: string[];
    questionsToAsk: string[];
    source?: string; // e.g., "ACOG", "March of Dimes"
    learnMoreUrl?: string; // Link to authoritative source
}

export const MILESTONE_DATA: MilestoneData[] = [
    {
        week: 4,
        title: "Positive Test & First Call",
        emoji: "🎉",
        description: "Congratulations! A positive pregnancy test is life-changing news. This is typically when hCG levels are high enough to detect. Your first step is scheduling an appointment with your healthcare provider to confirm the pregnancy and establish prenatal care.",
        partnerTip: "This is a moment you'll remember forever. Be present, express your feelings, and start discussing how you'll share the news with family.",
        pregnantTip: "Start taking prenatal vitamins with folic acid if you haven't already. Avoid alcohol, smoking, and limit caffeine to 200mg daily.",
        logistics: "Call your OB/GYN or midwife to schedule your first prenatal visit, usually around 8 weeks",
        whatToExpect: [
            "Emotional rollercoaster—excitement, anxiety, joy all at once",
            "Early symptoms may include fatigue, tender breasts, mild cramping",
            "It's normal to not 'feel' pregnant yet"
        ],
        questionsToAsk: [
            "When should I schedule my first prenatal appointment?",
            "What prenatal vitamins do you recommend?",
            "Are there any activities or foods I should avoid?"
        ],
        source: "American Pregnancy Association",
        learnMoreUrl: "https://americanpregnancy.org/getting-pregnant/pregnancy-test/"
    },
    {
        week: 8,
        title: "First Ultrasound (Dating)",
        emoji: "🩺",
        description: "Your first ultrasound is an exciting milestone! This dating scan confirms pregnancy viability, checks for a heartbeat, and establishes your due date. You'll likely see a tiny bean-shaped embryo and hear the heartbeat for the first time.",
        partnerTip: "Ask if you can attend this appointment—seeing the heartbeat together is incredibly bonding. Bring tissues, many partners get emotional.",
        pregnantTip: "You may need a full bladder for a clearer image. Don't be alarmed if they do a transvaginal ultrasound—it's common for early scans.",
        logistics: "Appointment duration: 20-30 minutes. Ask for printed photos or digital copies to share",
        whatToExpect: [
            "Confirmation of heartbeat (usually 120-160 bpm)",
            "Accurate due date calculation based on measurements",
            "Ruling out ectopic pregnancy or multiples",
            "Discussion of prenatal test options"
        ],
        questionsToAsk: [
            "Is the heartbeat within normal range?",
            "Does the size match my dates?",
            "When is my official due date?",
            "What genetic testing options are available?"
        ],
        source: "ACOG",
        learnMoreUrl: "https://www.acog.org/womens-health/faqs/ultrasound-exams"
    },
    {
        week: 12,
        title: "Nuchal Translucency Scan",
        emoji: "🔬",
        description: "The NT scan is an optional screening that measures fluid at the back of baby's neck. Combined with blood tests, it screens for chromosomal conditions like Down syndrome. This is also when many people choose to share pregnancy news publicly.",
        partnerTip: "Research the screening together beforehand so you're both informed. Remember: screening is not diagnostic—it shows risk levels, not definitive answers.",
        pregnantTip: "This is also a great ultrasound for photos! Baby looks much more like a tiny human now. Consider asking about NIPT blood testing for additional screening.",
        logistics: "Best done between weeks 11-13. Combines ultrasound with blood work for more accurate results",
        whatToExpect: [
            "Detailed ultrasound measuring baby's neck fold",
            "Blood draw for combined screening",
            "Results usually within 1-2 weeks",
            "Baby now has all major organs formed"
        ],
        questionsToAsk: [
            "What is my risk assessment result?",
            "What are my options if results are high-risk?",
            "Should I consider NIPT testing?",
            "When can we find out the sex?"
        ],
        source: "ACOG",
        learnMoreUrl: "https://www.acog.org/womens-health/faqs/prenatal-genetic-screening-tests"
    },
    {
        week: 14,
        title: "Second Trimester Begins",
        emoji: "🌟",
        description: "Welcome to the second trimester—often called the 'honeymoon phase' of pregnancy! Morning sickness typically eases, energy returns, and miscarriage risk drops significantly. Many people start feeling more confident sharing their news.",
        partnerTip: "Your partner may have more energy now—consider planning a babymoon trip or special dates before the third trimester. Start discussing nursery plans!",
        pregnantTip: "Enjoy this phase! You may notice your bump starting to show. This is a good time to look into childbirth education classes and start researching childcare.",
        logistics: "Prenatal visits typically shift to monthly. Start thinking about maternity leave planning",
        whatToExpect: [
            "Decreased nausea and fatigue for many",
            "Baby bump becoming visible",
            "Increased appetite and food cravings",
            "Possible return of libido"
        ],
        questionsToAsk: [
            "Am I gaining weight appropriately?",
            "What exercise is safe now?",
            "When should I register for childbirth classes?",
            "Are there any travel restrictions?"
        ],
        source: "American Pregnancy Association",
        learnMoreUrl: "https://americanpregnancy.org/healthy-pregnancy/week-by-week/second-trimester/"
    },
    {
        week: 20,
        title: "Anatomy Scan",
        emoji: "👶",
        description: "The mid-pregnancy anatomy scan is the most detailed ultrasound of your pregnancy! The technician will measure and examine every part of baby—brain, heart, spine, organs, and limbs. This is typically when you can find out the sex if you want.",
        partnerTip: "This appointment can take 45-60 minutes. Be patient, and don't worry if the technician is quiet—they're concentrating. Ask questions at the end.",
        pregnantTip: "Baby needs to be in a good position for all measurements. You may be asked to walk around, eat something sweet, or come back if baby is uncooperative.",
        logistics: "Schedule between weeks 18-22. Drink water but empty bladder right before. Ask about recording or extra photos",
        whatToExpect: [
            "Detailed measurements of all organs and limbs",
            "Placenta location and amniotic fluid levels",
            "Sex determination if desired",
            "Beautiful profile photos of baby",
            "Heart chambers and blood flow assessment"
        ],
        questionsToAsk: [
            "Is everything developing normally?",
            "Where is my placenta located?",
            "Are my fluid levels healthy?",
            "Is baby measuring on track?",
            "Do you see the sex?"
        ],
        source: "March of Dimes",
        learnMoreUrl: "https://www.marchofdimes.org/find-support/topics/pregnancy/prenatal-tests"
    },
    {
        week: 24,
        title: "Glucose Screening Test",
        emoji: "🍬",
        description: "The glucose screening tests for gestational diabetes, which affects about 6-9% of pregnancies. You'll drink a sugary drink and have blood drawn an hour later. If results are elevated, you'll do a longer 3-hour test to confirm.",
        partnerTip: "Offer to drive your partner to this appointment—some people feel lightheaded after fasting or from the sugar drink. Have a snack ready for after!",
        pregnantTip: "The drink tastes very sweet—some offices let you refrigerate it or drink it with ice. You don't need to fast for the 1-hour screen in most cases.",
        logistics: "1-hour test requires waiting at the office. Results usually within 24-48 hours",
        whatToExpect: [
            "Drinking 50g glucose solution (like very sweet soda)",
            "Blood draw exactly 1 hour after finishing",
            "Possible nausea or jitteriness—this is normal",
            "Results typically called within a few days"
        ],
        questionsToAsk: [
            "What glucose level is considered passing?",
            "If I fail, what's the next step?",
            "Can I eat before the test?",
            "How is gestational diabetes managed if diagnosed?"
        ],
        source: "ACOG",
        learnMoreUrl: "https://www.acog.org/womens-health/faqs/gestational-diabetes"
    },
    {
        week: 28,
        title: "Third Trimester Begins",
        emoji: "🏁",
        description: "You've entered the home stretch! The third trimester brings more frequent prenatal visits, increased baby movements, and preparation for birth. Baby is now viable outside the womb with medical support if born early.",
        partnerTip: "Start having regular conversations about the birth plan—preferences for pain management, who's in the delivery room, and what happens after birth.",
        pregnantTip: "Begin counting baby's kicks daily. Expect more discomfort—back pain, heartburn, and difficulty sleeping are common. Start sleeping on your side.",
        logistics: "Appointments increase to every 2 weeks, then weekly starting around week 36",
        whatToExpect: [
            "Increased frequency of prenatal visits",
            "More noticeable Braxton Hicks contractions",
            "Baby movements should be felt daily",
            "Growing belly makes everyday tasks harder",
            "Possible swelling in feet and ankles"
        ],
        questionsToAsk: [
            "How should I count kicks?",
            "What signs of preterm labor should I watch for?",
            "Should I start writing a birth plan?",
            "When do I get my Tdap vaccine?"
        ],
        source: "ACOG",
        learnMoreUrl: "https://www.acog.org/womens-health/faqs/how-your-fetus-grows-during-pregnancy"
    },
    {
        week: 28,
        title: "Tdap Vaccine",
        emoji: "💉",
        description: "The Tdap vaccine (tetanus, diphtheria, pertussis/whooping cough) is recommended for every pregnancy between weeks 27-36. It passes antibodies to baby, protecting them from whooping cough until they can get their own vaccines at 2 months.",
        partnerTip: "Partners and close family should also get Tdap if they haven't in the last 5 years—this creates a 'cocoon' of protection around your newborn.",
        pregnantTip: "Your arm may be sore for a day or two. This is the best gift you can give your newborn—babies are too young to be vaccinated and whooping cough can be deadly.",
        logistics: "Usually given at a prenatal appointment. Free with most insurance. Encourage grandparents to get boosted too",
        whatToExpect: [
            "Quick injection in the upper arm",
            "Mild soreness at injection site for 1-2 days",
            "No restrictions on activities after",
            "Baby will be protected at birth"
        ],
        questionsToAsk: [
            "Is the flu shot also recommended?",
            "Who else should get Tdap before baby arrives?",
            "Are there any side effects I should watch for?",
            "Is the COVID vaccine recommended during pregnancy?"
        ],
        source: "CDC",
        learnMoreUrl: "https://www.cdc.gov/pertussis/vaccines/tdap-pregnancy.html"
    },
    {
        week: 32,
        title: "Baby Shower & Prep",
        emoji: "🎁",
        description: "Baby showers are traditionally held between weeks 28-34. This is also a great time to finalize your nursery, wash baby clothes, and start stocking up on essentials. Baby is gaining weight rapidly now!",
        partnerTip: "Get involved in baby shower planning or prep! Take charge of assembling the crib, installing the car seat, and organizing the nursery.",
        pregnantTip: "Create a baby registry with essentials and nice-to-haves. Don't forget practical items like diapers, wipes, and postpartum supplies for yourself!",
        logistics: "Car seat should be installed by now. Take an infant CPR class if you haven't already",
        whatToExpect: [
            "Baby is about 4 pounds and 17 inches long",
            "Nesting instinct may kick in strongly",
            "Braxton Hicks become more noticeable",
            "Sleep becomes more difficult"
        ],
        questionsToAsk: [
            "Is baby in a head-down position?",
            "Should I start perineal massage?",
            "What are the signs I should go to the hospital?",
            "Can you recommend a pediatrician?"
        ],
        source: "March of Dimes",
        learnMoreUrl: "https://www.marchofdimes.org/find-support/topics/pregnancy/getting-ready-baby"
    },
    {
        week: 36,
        title: "Group B Strep Test",
        emoji: "🧪",
        description: "Group B Strep (GBS) is a common bacteria that's harmless to adults but can be dangerous for newborns. A simple swab test determines if you're a carrier. If positive, you'll receive IV antibiotics during labor to protect baby.",
        partnerTip: "If the test is positive, make sure you know the plan—typically IV antibiotics every 4 hours during labor. Add this to the hospital bag checklist reminder.",
        pregnantTip: "The swab is quick and painless—similar to a Pap smear. About 25% of women carry GBS, so testing positive is common and manageable.",
        logistics: "Results within a few days. If positive, notify the hospital when you arrive in labor",
        whatToExpect: [
            "Quick vaginal and rectal swab (you can do it yourself)",
            "Results typically within 48-72 hours",
            "If positive, antibiotics during labor—not now",
            "Baby is now considered early term at 37 weeks"
        ],
        questionsToAsk: [
            "What happens if I'm GBS positive?",
            "Will antibiotics affect breastfeeding?",
            "What if labor progresses too fast for antibiotics?",
            "How often will I have appointments now?"
        ],
        source: "CDC",
        learnMoreUrl: "https://www.cdc.gov/group-b-strep/about/index.html"
    },
    {
        week: 37,
        title: "Pack Hospital Bag",
        emoji: "🧳",
        description: "Your baby is now considered full-term! It's time to pack your hospital bag and have it ready by the door. Include comfort items, going-home outfits for you and baby, and practical necessities.",
        partnerTip: "Pack your own bag! Include snacks, phone chargers, change of clothes, and entertainment for what could be a long wait. Don't forget the car seat installation!",
        pregnantTip: "Include: comfortable robe, nursing bra, nipple cream, toiletries, going-home outfit, baby clothes in multiple sizes, phone charger, and any comfort items like a pillow.",
        logistics: "Car seat must be properly installed. Know the fastest route to the hospital and backup routes",
        whatToExpect: [
            "Baby could arrive any time now",
            "Cervical checks may begin at appointments",
            "Increased pelvic pressure as baby 'drops'",
            "Practice breathing and relaxation techniques"
        ],
        questionsToAsk: [
            "Is my cervix starting to dilate?",
            "What position is baby in?",
            "When should I head to the hospital?",
            "What's the hospital visitor policy?"
        ],
        source: "American Pregnancy Association",
        learnMoreUrl: "https://americanpregnancy.org/healthy-pregnancy/labor-and-birth/packing-your-bag-for-the-hospital/"
    },
    {
        week: 40,
        title: "Due Date",
        emoji: "🎂",
        description: "You've reached your due date! Remember, it's just an estimate—only about 5% of babies arrive exactly on their due date. Baby will come when ready, and your care team will monitor you closely if you go past 40 weeks.",
        partnerTip: "Be patient and supportive. Don't ask 'any signs yet?' repeatedly—focus on keeping your partner comfortable and distracted. Have the hospital bag in the car!",
        pregnantTip: "Stay active, try natural induction methods if cleared by your doctor (walking, spicy food, etc.), and rest when you can. Trust your body and your baby's timing.",
        logistics: "If no labor by 41 weeks, you'll discuss induction options. Stay in close contact with your care team",
        whatToExpect: [
            "Baby is fully developed and ready for birth",
            "Average weight is 7-8 pounds",
            "You may feel 'lightening' as baby drops lower",
            "Signs of labor: contractions, water breaking, bloody show"
        ],
        questionsToAsk: [
            "How long past my due date will you let me go?",
            "What are the induction options if needed?",
            "Should I come in for monitoring?",
            "What pain management options do you offer?"
        ],
        source: "ACOG",
        learnMoreUrl: "https://www.acog.org/womens-health/faqs/labor-induction"
    }
];

/**
 * Get full milestone data by week and title
 */
export function getFullMilestoneData(week: number, title: string): MilestoneData | undefined {
    return MILESTONE_DATA.find(m => m.week === week && m.title === title);
}
